"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PostData } from '@/services/api';
import PostCard from '@/components/ui/PostCard/Postcard';

interface VirtualizedPostsProps {
  posts: PostData[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  className?: string;
  onMenuClick?: (postId: string, buttonRect: DOMRect) => void;
  isMenuOpen?: boolean;
  onCloseMenu?: () => void;
  selectedFeed?: "for you" | "Highlight";
  focusedPostId?: string | null;
  isCommunityAdmin?: boolean;
  /** When false (default), community posts show author + "to All" without community icon. When true, show community icon + name (profile Joydrops Given). */
  showCommunityHeader?: boolean;
  /** Card background: 'white' for community page, 'grey' for profile. */
  cardBackground?: 'white' | 'grey';
  /** Admin user IDs for the current community (single-community feed). */
  communityAdminIds?: string[];
  /** Admin user IDs per community (multi-community feed e.g. home). */
  communityAdminIdsByCommunity?: Record<string, string[]>;
  /** Home-only typography variant for community identity text. */
  useContentFontForCommunityIdentity?: boolean;
}

const ITEM_HEIGHT = 500; // Estimated height of each post card - increased to prevent overlapping
const BUFFER_SIZE = 5; // Number of items to render outside viewport

export const VirtualizedPosts: React.FC<VirtualizedPostsProps> = ({
  posts,
  loading,
  hasMore,
  onLoadMore,
  onRefresh,
  className = "",
  onMenuClick,
  isMenuOpen = false,
  onCloseMenu,
  selectedFeed,
  focusedPostId,
  isCommunityAdmin = false,
  showCommunityHeader = false,
  cardBackground,
  communityAdminIds,
  communityAdminIdsByCommunity,
  useContentFontForCommunityIdentity = false,
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);

  // Ensure posts is always an array to prevent slice errors and dedupe by ID
  const safePosts = Array.isArray(posts)
    ? posts.filter((post, index, self) => {
        // sender object may be omitted by the API; senderID is required on PostData
        const hasSenderRef = Boolean(post.senderID || post.sender);
        return hasSenderRef && self.findIndex((p) => p.id === post.id) === index;
      })
    : [];

  // Calculate visible range
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER_SIZE);
  const endIndex = Math.min(
    safePosts.length - 1,
    Math.ceil((scrollTop + containerHeight) / ITEM_HEIGHT) + BUFFER_SIZE
  );

  const visiblePosts = safePosts.slice(startIndex, endIndex + 1);
  const totalHeight = safePosts.length * ITEM_HEIGHT;
  const offsetY = startIndex * ITEM_HEIGHT;

  // Handle scroll events
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setScrollTop(target.scrollTop);
  }, []);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, onLoadMore]);

  // Update container height on resize
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Scroll to focused post
  useEffect(() => {
    if (focusedPostId && safePosts.length > 0) {
      // Delay slightly to ensure DOM is ready
      const timer = setTimeout(() => {
        const element = document.getElementById(`post-${focusedPostId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [focusedPostId, safePosts]);

  // Pull-to-refresh functionality
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const startY = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, [scrollTop]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (isPulling && scrollTop === 0) {
      const currentY = e.touches[0].clientY;
      const distance = Math.max(0, currentY - startY.current);
      setPullDistance(Math.min(distance, 100));
    }
  }, [isPulling, scrollTop]);

  const handleTouchEnd = useCallback(() => {
    if (isPulling && pullDistance > 50) {
      onRefresh();
    }
    setPullDistance(0);
    setIsPulling(false);
  }, [isPulling, pullDistance, onRefresh]);

  return (
    <div className={`relative ${className}`}>
      {/* Pull to refresh indicator */}
      {isPulling && (
        <div
          className="absolute top-0 left-0 right-0 flex items-center justify-center bg-blue-50 text-blue-600 transition-all duration-200 z-10"
          style={{ height: pullDistance, transform: `translateY(-${100 - pullDistance}px)` }}
        >
          {pullDistance > 50 ? 'Release to refresh' : 'Pull to refresh'}
        </div>
      )}

      <div
        ref={containerRef}
        className="overflow-visible"
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Simple posts container without virtualization for now to fix overlapping */}
        <div className="flex flex-col gap-6">
          {safePosts.map((post) => (
            <div
              key={post.id}
              id={`post-${post.id}`}
              className={`w-full transition-all duration-300 ${focusedPostId === post.id ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
                }`}
            >
              <PostCard
                post={post}
                highlighted={selectedFeed === "Highlight"}
                showCommunityHeader={showCommunityHeader}
                cardBackground={cardBackground}
                senderIsCommunityAdmin={!!(post.communityId && (communityAdminIdsByCommunity?.[post.communityId] ?? communityAdminIds)?.includes(post.senderID))}
                receiverIsCommunityAdmin={!!(post.communityId && (post.receiverID || post.receiver?.uid) && (communityAdminIdsByCommunity?.[post.communityId] ?? communityAdminIds)?.includes(post.receiverID || post.receiver?.uid || ''))}
                communityAdminIds={post.communityId ? (communityAdminIdsByCommunity?.[post.communityId] ?? communityAdminIds) : undefined}
                useContentFontForCommunityIdentity={useContentFontForCommunityIdentity}
                onLike={() => {
                  // Handle like functionality
                  console.log('Celebrate post:', post.id);
                }}
                onComment={() => {
                  // Handle comment functionality
                  console.log('Comment on post:', post.id);
                }}
                onShare={() => {
                  // Handle share functionality
                  console.log('Share post:', post.id);
                }}
                onMenuClick={onMenuClick}
                isMenuOpen={isMenuOpen}
                onCloseMenu={onCloseMenu}
              />
            </div>
          ))}
        </div>

        {/* Loading indicator */}
        <div ref={loadingRef} className="flex justify-center p-4">
          {loading && (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Loading posts...</span>
            </div>
          )}
          {!loading && !hasMore && safePosts.length > 0 && (
            <div className="text-gray-500 text-center py-4">
              No more posts to load
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
